import type { ReactNode } from 'react';

import { Icon } from '@components/Icon';
import type { IconType } from '@design-tokens/icons';
import { buildClassName } from '@utils';

import './Toast.css';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

const DEFAULT_ICON: Record<ToastVariant, IconType> = {
  success: 'checkmark',
  error: 'exclamation-circle',
  warning: 'exclamation-triangle',
  info: 'exclamation-circle',
};

const ICON_COLOR: Record<ToastVariant, 'green' | 'red' | 'orange' | 'blue'> = {
  success: 'green',
  error: 'red',
  warning: 'orange',
  info: 'blue',
};

export interface ToastProps {
  /** Visual variant (status, error, etc.) */
  variant: ToastVariant;
  /** Message text */
  message: string;
  /** Show icon; default true */
  showIcon?: boolean;
  /** Override default icon for variant */
  iconType?: IconType;
  /** Called when toast is dismissed (e.g. auto or close) */
  onDismiss?: () => void;
  /** Optional close button; if not set, no close control */
  dismissible?: boolean;
  /** Content to render instead of message (rare) */
  children?: ReactNode;
}

export function Toast({
  variant,
  message,
  showIcon = true,
  iconType,
  onDismiss,
  dismissible = false,
  children,
}: ToastProps) {
  const icon = iconType ?? DEFAULT_ICON[variant];
  const colorVariant = ICON_COLOR[variant];

  const rootClasses = buildClassName(
    'bhg-toast',
    `bhg-toast--${variant}`,
    dismissible && 'bhg-toast--dismissible'
  );

  return (
    <div
      className={rootClasses}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {showIcon && (
        <span className="bhg-toast__icon" aria-hidden>
          <Icon
            type={icon}
            colorVariant={colorVariant}
            size="lg"
            style="outlined"
            backgroundShape="circle"
          />
        </span>
      )}
      <span className="bhg-toast__message">{children ?? message}</span>
      {dismissible && onDismiss && (
        <button
          type="button"
          className="bhg-toast__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <span aria-hidden>×</span>
        </button>
      )}
    </div>
  );
}
